import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../utils/axiosConfig';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8083/api';

// Helper functions for category transformation
const getCategoryDescription = (categoryName) => {
  const descriptions = {
    'Marble': 'Luxurious marble slabs from worldwide',
    'Granite': 'Durable granite for all applications',
    'Tiles': 'Modern tiles for contemporary spaces',
    'Stone': 'Authentic natural stone varieties',
    'Ceramic': 'High-quality ceramic products',
    'Porcelain': 'Premium porcelain materials',
    'Limestone': 'Natural limestone for construction',
    'Sandstone': 'Beautiful sandstone varieties',
    'Slate': 'Elegant slate for modern designs',
    'Travertine': 'Classic travertine stone',
  };
  
  // Find matching description or create a generic one
  const matchingKey = Object.keys(descriptions).find(key => 
    categoryName.toLowerCase().includes(key.toLowerCase())
  );
  
  return matchingKey ? descriptions[matchingKey] : `Premium ${categoryName.toLowerCase()} for all your construction needs`;
};

const getCategoryColor = (index) => {
  const colors = [
    '#E8F5E8', // Light green
    '#FFF3E0', // Light orange
    '#E3F2FD', // Light blue
    '#F3E5F5', // Light purple
    '#FFF8E1', // Light yellow
    '#FFEBEE', // Light red
    '#E0F2F1', // Light teal
    '#F1F8E9', // Light lime
  ];
  
  return colors[index % colors.length];
};

// Async thunks for product operations
export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async ({ page = 0, size = 20, category, search, sortBy = 'createdAt', sortDir = 'desc', minPrice, maxPrice, minQuantity, maxQuantity, material, finish, origin, status, approvalStatus, priceRange, groupName, productIds }, { rejectWithValue }) => {
    try {
      let url = `${API_BASE_URL}/products?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`;

      // ID-based GROUP retrieval — exact products from autocomplete grouping
      if (productIds && productIds.length > 0) {
        url += `&product_ids=${productIds.join(',')}`;
      } else if (groupName) {
        // Fallback: regex-based group filter (kept for direct URL navigation)
        url += `&group_name=${encodeURIComponent(groupName)}`;
      }

      if (category) {
        url += `&category=${encodeURIComponent(category)}`;
      }

      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      
      // Support priceRange array as a convenience from UI filters
      if ((minPrice === undefined || maxPrice === undefined) && Array.isArray(priceRange) && priceRange.length === 2) {
        const [min, max] = priceRange;
        if (min !== undefined && min !== null) {
          minPrice = min;
        }
        if (max !== undefined && max !== null) {
          maxPrice = max;
        }
      }
      
      if (minPrice !== undefined) {
        url += `&minPrice=${encodeURIComponent(minPrice)}`;
      }
      if (maxPrice !== undefined) {
        url += `&maxPrice=${encodeURIComponent(maxPrice)}`;
      }
      if (minQuantity !== undefined) {
        url += `&minQuantity=${encodeURIComponent(minQuantity)}`;
      }
      if (maxQuantity !== undefined) {
        url += `&maxQuantity=${encodeURIComponent(maxQuantity)}`;
      }
      if (material) {
        url += `&material=${encodeURIComponent(material)}`;
      }
      if (finish) {
        url += `&finish=${encodeURIComponent(finish)}`;
      }
      if (origin) {
        url += `&origin=${encodeURIComponent(origin)}`;
      }
      if (status) {
        url += `&status=${encodeURIComponent(status)}`;
      }
      if (approvalStatus) {
        url += `&approvalStatus=${encodeURIComponent(approvalStatus)}`;
      }
      
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch products'
      );
    }
  }
);

export const fetchProductById = createAsyncThunk(
  'products/fetchProductById',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/products/${productId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch product details'
      );
    }
  }
);

export const fetchFeaturedProducts = createAsyncThunk(
  'products/fetchFeaturedProducts',
  async ({ size = 8 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/products/featured?size=${size}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch featured products'
      );
    }
  }
);

export const fetchProductsByCategory = createAsyncThunk(
  'products/fetchProductsByCategory',
  async ({ category, page = 0, size = 20 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/products/category/${category}?page=${page}&size=${size}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch products by category'
      );
    }
  }
);

export const searchProducts = createAsyncThunk(
  'products/searchProducts',
  async ({ searchTerm, page = 0, size = 20, filters = {} }, { rejectWithValue }) => {
    try {
      let url = `${API_BASE_URL}/products/search?searchTerm=${searchTerm}&page=${page}&size=${size}`;
      
      // Add filters to URL
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          url += `&${key}=${filters[key]}`;
        }
      });
      
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to search products'
      );
    }
  }
);

export const fetchProductCategories = createAsyncThunk(
  'products/fetchProductCategories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/products/categories`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch categories'
      );
    }
  }
);

export const fetchSimilarProducts = createAsyncThunk(
  'products/fetchSimilarProducts',
  async ({ productId, category, size = 4 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/products/similar?productId=${productId}&category=${category}&size=${size}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch similar products'
      );
    }
  }
);

const productSlice = createSlice({
  name: 'products',
  initialState: {
    // Product lists
    products: [],
    featuredProducts: [],
    similarProducts: [],
    
    // Current product
    currentProduct: null,
    
    // Categories
    categories: [],
    
    // Pagination
    currentPage: 0,
    totalPages: 0,
    totalElements: 0,
    pageSize: 20,
    
    // Filters and search
    filters: {
      category: '',
      priceRange: [0, 5000],
      material: '',
      finish: '',
      origin: '',
      sortBy: 'createdAt',
      sortDir: 'desc',
    },
    searchTerm: '',
    
    // Loading states
    loading: false,
    featuredLoading: false,
    categoryLoading: false,
    similarLoading: false,
    
    // Error states
    error: null,
    
    // UI states
    viewMode: 'grid', // 'grid' or 'list'
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentProduct: (state, action) => {
      state.currentProduct = action.payload;
    },
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        category: '',
        priceRange: [0, 5000],
        material: '',
        finish: '',
        origin: '',
        sortBy: 'createdAt',
        sortDir: 'desc',
      };
    },
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    clearSearchTerm: (state) => {
      state.searchTerm = '';
    },
    setViewMode: (state, action) => {
      state.viewMode = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    clearProducts: (state) => {
      state.products = [];
      state.currentPage = 0;
      state.totalPages = 0;
      state.totalElements = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Products
      .addCase(fetchProducts.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        // Remember the newest request; older responses that arrive later are ignored below
        state.latestProductsRequestId = action.meta.requestId;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        // Ignore stale responses (filters changed again before this request finished)
        if (action.meta.requestId !== state.latestProductsRequestId) return;
        state.loading = false;
        state.products = action.payload.content || action.payload;
        state.currentPage = action.payload.number || 0;
        state.totalPages = action.payload.totalPages || 1;
        state.totalElements = action.payload.totalElements || state.products.length;
        state.pageSize = action.payload.size || 20;
        state.error = null;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestProductsRequestId) return;
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch Product by ID
      .addCase(fetchProductById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProduct = action.payload;
        state.error = null;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch Featured Products
      .addCase(fetchFeaturedProducts.pending, (state) => {
        state.featuredLoading = true;
        state.error = null;
      })
      .addCase(fetchFeaturedProducts.fulfilled, (state, action) => {
        state.featuredLoading = false;
        state.featuredProducts = action.payload.content || action.payload;
        state.error = null;
      })
      .addCase(fetchFeaturedProducts.rejected, (state, action) => {
        state.featuredLoading = false;
        state.error = action.payload;
      })
      
      // Fetch Products by Category
      .addCase(fetchProductsByCategory.pending, (state) => {
        state.categoryLoading = true;
        state.error = null;
      })
      .addCase(fetchProductsByCategory.fulfilled, (state, action) => {
        state.categoryLoading = false;
        state.products = action.payload.content || action.payload;
        state.currentPage = action.payload.number || 0;
        state.totalPages = action.payload.totalPages || 1;
        state.totalElements = action.payload.totalElements || state.products.length;
        state.error = null;
      })
      .addCase(fetchProductsByCategory.rejected, (state, action) => {
        state.categoryLoading = false;
        state.error = action.payload;
      })
      
      // Search Products
      .addCase(searchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload.content || action.payload;
        state.currentPage = action.payload.number || 0;
        state.totalPages = action.payload.totalPages || 1;
        state.totalElements = action.payload.totalElements || state.products.length;
        state.error = null;
      })
      .addCase(searchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch Categories
      .addCase(fetchProductCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductCategories.fulfilled, (state, action) => {
        state.loading = false;
        // Transform the API response (List of Maps with name, count, description, and color keys)
        // into the format expected by the UI
        const categoriesData = action.payload;
        const transformedCategories = categoriesData.map((categoryItem, index) => ({
          id: index + 1,
          name: categoryItem.name,
          description: categoryItem.description || getCategoryDescription(categoryItem.name),
          image: `/images/${categoryItem.name.toLowerCase().replace(/\s+/g, '-')}-category.jpg`,
          count: categoryItem.count,
          color: categoryItem.color || getCategoryColor(index),
        }));
        state.categories = transformedCategories;
        state.error = null;
      })
      .addCase(fetchProductCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch Similar Products
      .addCase(fetchSimilarProducts.pending, (state) => {
        state.similarLoading = true;
        state.error = null;
      })
      .addCase(fetchSimilarProducts.fulfilled, (state, action) => {
        state.similarLoading = false;
        state.similarProducts = action.payload.content || action.payload;
        state.error = null;
      })
      .addCase(fetchSimilarProducts.rejected, (state, action) => {
        state.similarLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentProduct,
  clearCurrentProduct,
  setFilters,
  clearFilters,
  setSearchTerm,
  clearSearchTerm,
  setViewMode,
  setCurrentPage,
  clearProducts,
} = productSlice.actions;

export default productSlice.reducer;